import React, { useState, useEffect, useMemo } from 'react';
import { Table, Spinner, UncontrolledTooltip } from 'reactstrap';
import { useTable, useSortBy } from 'react-table';
import { Icon } from '@assets';
import { propertyTypes, bathTypes } from '@sparklib';

const LS_KEY = 'fp_visible_columns';

const ALL_COLUMNS = [
  { id: 'name',               Header: 'Community',     defaultVisible: true  },
  { id: 'phone',              Header: 'Phone',         defaultVisible: true  },
  { id: 'email',              Header: 'Email',         defaultVisible: true  },
  { id: 'specials',           Header: 'Specials',      defaultVisible: false },
  { id: 'neighborhood',       Header: 'Neighborhood',  defaultVisible: true  },
  { id: 'built_year',         Header: 'Built / Renov', defaultVisible: true  },
  { id: 'bed',                Header: 'Bed / Bath',    defaultVisible: true  },
  { id: 'min_price',          Header: 'Rent',          defaultVisible: true  },
  { id: 'min_sqft',           Header: 'Sqft',          defaultVisible: true  },
  { id: 'send_escort',        Header: 'Send',          defaultVisible: true  },
  { id: 'escort',             Header: 'Escort',        defaultVisible: true  },
  { id: 'bonus',              Header: 'Bonus',         defaultVisible: true  },
  { id: 'address',            Header: 'Address',       defaultVisible: false },
  { id: 'management_company', Header: 'Management Co', defaultVisible: false },
  { id: 'addon_items',        Header: 'Amenities',     defaultVisible: false },
];

function formatPrice(val) {
  if (!val) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(val);
}

const EMAIL_MAX = 28;

function EmailCell({ value, row }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <>—</>;
  const tipId     = `email-tip-${row.original.id}`;
  const copyBtnId = `copy-btn-${row.original.id}`;
  const truncated = value.length > EMAIL_MAX ? value.slice(0, EMAIL_MAX).trimEnd() + '…' : value;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span
      onClick={e => e.stopPropagation()}
      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
    >
      <a id={tipId} href={`mailto:${value}`} onClick={e => e.stopPropagation()}>{truncated}</a>
      {value.length > EMAIL_MAX && (
        <UncontrolledTooltip target={tipId} placement='top'>{value}</UncontrolledTooltip>
      )}
      <span id={copyBtnId} style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={handleCopy}>
        <Icon
          icon='copy'
          style={{ color: copied ? 'var(--theme-green, #27ae60)' : 'var(--gray-500)', fontSize: '12px', flexShrink: 0 }}
        />
      </span>
      <UncontrolledTooltip target={copyBtnId} placement='top'>
        {copied ? 'Email copied!' : 'Copy email'}
      </UncontrolledTooltip>
    </span>
  );
}

// Returns only the available floor plans that match all active filter criteria.
function filterFloorPlans(floorPlans, af) {
  if (!af || !floorPlans) return floorPlans;
  const { bedTypes, minPrice, maxPrice, minSqft, availFrom, availTo } = af;
  const hasFilter = bedTypes?.length || minPrice > 0 || maxPrice > 0 || minSqft > 0 || availFrom || availTo;
  if (!hasFilter) return floorPlans;
  // Compute once for the avail_from check below
  const todayStart = availFrom ? new Date(new Date().setHours(0, 0, 0, 0)) : null;
  return floorPlans.filter(fp => {
    if (!fp.isAvailable) return false;
    if (bedTypes?.length && !bedTypes.includes(fp.bed)) return false;
    const rent = fp.rentMin || fp.rentMax || 0;
    if (maxPrice > 0 && rent > 0 && rent > maxPrice) return false;
    const rentTop = fp.rentMax || fp.rentMin || 0;
    if (minPrice > 0 && rentTop > 0 && rentTop < minPrice) return false;
    if (minSqft > 0 && fp.sqftMin > 0 && fp.sqftMin < minSqft) return false;
    // avail_from: only exclude if available_to is in the future AND closes before move-in date.
    // A past available_to means the unit has been open since that date and is still available now.
    if (availFrom && fp.availableTo) {
      const fpAvailTo = new Date(fp.availableTo);
      if (fpAvailTo >= todayStart && fpAvailTo < availFrom) return false;
    }
    // avail_to: floor plan must open availability before user's deadline
    if (availTo && fp.availableFrom && new Date(fp.availableFrom) > availTo) return false;
    return true;
  });
}

function BedBathCell({ floorPlans, activeFilters }) {
  const displayPlans = filterFloorPlans(floorPlans, activeFilters);
  if (!displayPlans || !displayPlans.length) return <>—</>;
  return (
    <>
      {propertyTypes(displayPlans)} <Icon icon='bed' /><br />
      {bathTypes(displayPlans)} <Icon icon='bath' />
    </>
  );
}

function makeColumns(visibleIds, activeFilters) {
  return ALL_COLUMNS
    .filter(col => visibleIds.includes(col.id))
    .map(col => {
      const base = { id: col.id, Header: col.Header, disableSortBy: true };
      switch (col.id) {

        case 'name':
          return {
            ...base,
            accessor: 'name',
            disableSortBy: false,
            colStyle: { minWidth: '150px', whiteSpace: 'nowrap' },
            Cell: ({ value }) => <span className='font-weight-600'>{value || '—'}</span>,
          };

        case 'phone':
          return {
            ...base,
            accessor: 'phone',
            Cell: ({ value }) => value
              ? <a href={`tel:${value}`} onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>{value}</a>
              : '—',
          };

        case 'email':
          return {
            ...base,
            accessor: 'email',
            colStyle: { whiteSpace: 'nowrap', minWidth: '260px' },
            Cell: EmailCell,
          };

        case 'specials': {
          const SPEC_MAX = 50;
          return {
            ...base,
            accessor: 'specials',
            colStyle: { minWidth: '200px' },
            Cell: ({ value, row }) => {
              if (!value?.trim()) return '—';
              const tipId = `specials-tip-${row.original.id}`;
              const truncated = value.length > SPEC_MAX
                ? value.slice(0, SPEC_MAX).trimEnd() + '…'
                : value;
              return (
                <>
                  <span
                    id={tipId}
                    className='badge badge-warning'
                    style={{ whiteSpace: 'normal', cursor: 'default', fontWeight: 400 }}
                  >
                    {truncated}
                  </span>
                  {value.length > SPEC_MAX && (
                    <UncontrolledTooltip target={tipId} placement='top' style={{ maxWidth: 320 }}>
                      {value}
                    </UncontrolledTooltip>
                  )}
                </>
              );
            },
          };
        }

        case 'neighborhood':
          return {
            ...base,
            accessor: row => row.neighborhood?.name,
            colStyle: { minWidth: '130px' },
            Cell: ({ value }) => value || '—',
          };

        case 'built_year':
          return {
            ...base,
            accessor: 'builtYear',
            disableSortBy: false,
            Cell: ({ row }) => {
              const { builtYear, renovated } = row.original;
              if (!builtYear && !renovated) return '—';
              return (
                <div style={{ lineHeight: 1.4 }}>
                  {builtYear && <div>Built {builtYear}</div>}
                  {renovated && <div>Renov {renovated}</div>}
                </div>
              );
            },
          };

        case 'bed':
          return {
            ...base,
            accessor: row => row.floorPlans,
            Cell: ({ value }) => <BedBathCell floorPlans={value} activeFilters={activeFilters} />,
          };

        case 'min_price':
          return {
            ...base,
            accessor: 'minPrice',
            disableSortBy: false,
            Cell: ({ row }) => {
              const plans = filterFloorPlans(row.original.floorPlans, activeFilters);
              const hasPlans = plans && plans.length > 0;
              const lo = hasPlans
                ? Math.min(...plans.map(fp => fp.rentMin || fp.rentMax || 0).filter(Boolean))
                : row.original.minPrice;
              const hi = hasPlans
                ? Math.max(...plans.map(fp => fp.rentMax || fp.rentMin || 0).filter(Boolean))
                : row.original.maxPrice;
              if (lo && hi && lo !== hi)
                return `${formatPrice(lo)} – ${formatPrice(hi)}`;
              return formatPrice(lo) || formatPrice(hi) || '—';
            },
          };

        case 'min_sqft':
          return {
            ...base,
            accessor: 'minSqft',
            disableSortBy: false,
            Cell: ({ row }) => {
              const plans = filterFloorPlans(row.original.floorPlans, activeFilters);
              const hasPlans = plans && plans.length > 0;
              const sqfts = hasPlans
                ? plans.map(fp => fp.sqftMin).filter(Boolean)
                : [];
              const minSqft = sqfts.length > 0 ? Math.min(...sqfts) : row.original.minSqft;
              const maxSqft = sqfts.length > 0 ? Math.max(...sqfts) : row.original.maxSqft;
              if (!minSqft) return '—';
              const min = minSqft.toLocaleString();
              const max = maxSqft && maxSqft !== minSqft ? maxSqft.toLocaleString() : null;
              return <>{max ? `${min}-${max}` : min}ft<sup>2</sup></>;
            },
          };

        case 'send_escort':
          return {
            ...base,
            accessor: 'sendEscort',
            disableSortBy: false,
            Cell: ({ value }) => value != null ? `${value}%` : '—',
          };

        case 'escort':
          return {
            ...base,
            accessor: 'escort',
            disableSortBy: false,
            Cell: ({ value }) => value != null ? `${value}%` : '—',
          };

        case 'bonus':
          return {
            ...base,
            accessor: 'bonus',
            disableSortBy: false,
            Cell: ({ value }) => value || '—',
          };

        case 'address':
          return {
            ...base,
            accessor: 'address',
            disableSortBy: false,
            Cell: ({ value }) => value || '—',
          };

        case 'management_company':
          return {
            ...base,
            accessor: 'managementCompany',
            disableSortBy: false,
            Cell: ({ value }) => value || '—',
          };

        case 'addon_items':
          return {
            ...base,
            accessor: 'addonItems',
            Cell: ({ value }) => {
              if (!value || !value.length) return '—';
              return (
                <span>
                  {value.slice(0, 3).join(', ')}
                  {value.length > 3 && <small className='text-muted'> +{value.length - 3}</small>}
                </span>
              );
            },
          };

        default:
          return base;
      }
    });
}

function loadVisibleIds() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      // Strip 'zip' and 'commission' from any saved preference (removed columns)
      const parsed = JSON.parse(saved);
      return parsed.filter(id => id !== 'zip' && id !== 'commission');
    }
  } catch (_) {}
  return ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id);
}

export default function PropertyTable({
  properties,
  loading,
  sentinelRef,
  onColumnsReady,
  selectedProperties,
  toggleSelection,
  onRowClick,
  activeFilters,
}) {
  const [visibleIds, setVisibleIds] = useState(loadVisibleIds);

  const heartColumn = useMemo(() => ({
    id: 'heart',
    disableSortBy: true,
    colStyle: { width: '48px', minWidth: '48px', maxWidth: '48px' },
    Cell: ({ row }) => {
      const isSelected = (selectedProperties || []).some(
        p => Number(p.propertyId) === Number(row.original.id)
      );
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            toggleSelection && toggleSelection(row.original);
          }}
          style={{ cursor: 'pointer', padding: '2px 0px' }}
        >
          {isSelected
            ? <Icon icon='heart' size='lg' />
            : <Icon size='lg' icon={['far', 'heart']} />
          }
        </div>
      );
    },
  }), [selectedProperties, toggleSelection]);

  const columns = useMemo(
    () => [heartColumn, ...makeColumns(visibleIds, activeFilters)],
    [heartColumn, visibleIds, activeFilters]
  );
  const data = useMemo(() => properties || [], [properties]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy);

  // Expose column state + toggle + restore to parent toolbar
  useEffect(() => {
    if (!onColumnsReady) return;
    const opts = ALL_COLUMNS.map(c => ({ value: c.id, label: c.Header }));
    const defaultIds = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id);
    const toggleFn = (option) => {
      setVisibleIds(prev => {
        const next = prev.includes(option.value)
          ? prev.filter(id => id !== option.value)
          : [...prev, option.value];
        try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (_) {}
        return next;
      });
    };
    const restoreFn = () => {
      setVisibleIds(defaultIds);
      try { localStorage.removeItem(LS_KEY); } catch (_) {}
    };
    onColumnsReady(opts, visibleIds, toggleFn, restoreFn);
  }, [visibleIds]);

  if (!loading && (!properties || properties.length === 0)) {
    return (
      <div className='fp-empty'>
        <p>No properties found. Try adjusting your filters.</p>
      </div>
    );
  }

  return (
    <div>
      <Table className='client-list text-left min-w-900' style={{ border: '0px solid var(--gray-700)' }} {...getTableProps()}>
        <thead>
          {headerGroups.map((hg, j) => (
            <tr key={j} {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col, i) => (
                <th
                  key={i}
                  {...col.getHeaderProps(col.canSort ? col.getSortByToggleProps() : {})}
                  style={{ whiteSpace: 'nowrap', cursor: col.canSort ? 'pointer' : 'default', ...col.colStyle }}
                >
                  {col.render('Header')}
                  <span>
                    {col.isSorted ? (
                      col.isSortedDesc
                        ? <>&nbsp;<Icon icon='sort-down' size='lg' /></>
                        : <>&nbsp;<Icon icon='sort-up' size='lg' /></>
                    ) : col.canSort ? (
                      <>&nbsp;<Icon icon='sort' size='lg' /></>
                    ) : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                key={i}
                {...row.getRowProps()}
                style={{ cursor: 'pointer' }}
                onClick={() => onRowClick && onRowClick(row.original)}
              >
                {row.cells.map((cell, j) => (
                  <td key={j} {...cell.getCellProps()} style={cell.column.colStyle}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div ref={sentinelRef} style={{ height: 1 }} />

      {loading && (
        <div className='text-center py-3'>
          <Spinner size='sm' /> Loading...
        </div>
      )}
    </div>
  );
}
